import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { getDevicePref, setSpLogLvl, spErrorTags, spLogTags, TOrgTheme } from "@eventr/common";
import { ColoursObj, generateTheme, TColourSchemes } from "@eventr/common-storybook";
import { createTheme, ThemeProvider } from "@mui/material";
import { captureException } from "@sentry/capacitor";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LocalExpenseManager from "feat/editItinerary/expenses/local-expense-store/LocalExpenseManager";
import { FavouriteSyncManager } from "feat/favourites/FavouriteSyncManager";
import { NoteSyncManager } from "feat/notes/NoteSyncManager";
import { UsersRequiredAddiInfoDialogHandler } from "feat/profile/UsersRequiredAddiInfoDialogHandler";
import { lazy, use, useEffect, useMemo, useRef, useState } from "react";
import { deviceDetect } from "react-device-detect";
import { ErrorBoundary } from "react-error-boundary";
import { I18nextProvider } from "react-i18next";
import { bs, useBoundStore } from "state/bound.store";
import AppLoadingSpinner from "./assets/animated/appLoadingSpinner";
import { registerListeners } from "./feat/appState/appStateListener";
import "./feat/appState/deviceInfo";
import { setStatusBarStyleDark, setStatusBarStyleLight } from "./feat/appState/statusBar";
import { checkCalInitialPermissions } from "./feat/calendar/calendarProvider";
import "./feat/connectivity/connectivityListener";
import { DevRoutesSplit } from "./feat/developerMenu/devRoutesSplit";
import { ErrorPage } from "./feat/developerMenu/errorPage";
import { setCollectionEnabled } from "./feat/firebase/firebase";
import { GuestRoutesSplit } from "./feat/guest/guestRouteSplit";
import { ThemeRemountGate } from "./feat/nativeNavigation/ThemeRemountGate";
import { AppChatProvider } from "./feat/rocketchat/AppChatProvider";
import { RocketChatProvider } from "./feat/rocketchat/rocketChatProvider";
import { themeOverride } from "./feat/themeOverrides";
import { i18nPromise } from "./i18n/i18next";
import { LanguageRemountGate } from "./i18n/LanguageRemountGate";
import { AppRoutes } from "./routes";
import { initSentry } from "./sentry";
import { sweepStaleImagePreviews } from "./state/overlayState/nativeImagePreview";
import { OverlayWrapper } from "./state/overlayState/overlayWrapper";
import { ScrollWrapper } from "./state/scrollState/scrollWrapper";
import { selectEffectiveTheme } from "./state/themeState/effectiveTheme";
import { useStorageStore } from "./state/storage/storage.store";
import { EFFECTIVE_THEME_PREF, setThemeDevicePref, useThemeContext } from "./state/themeState/themeContext";
import SkinFadeLayer from "./state/themeState/SkinFadeLayer";
import { chooseApplyMode, themeSignature, useSkinFadeStore } from "./state/themeState/skin-fade.store";
import { useWindowStore } from "./state/windowState/window.store";
import { delay, getDevicePrefName } from "./utils";
import { reactAppStage } from "./utils/reactAppStage";
import { FILE_VIEWER_PATH } from "./feat/fileViewer/open-file-viewer";
const DEBUG_STAGES = ["dev", "local"];

function setDefaultStage() {
  spLogTags("Current app version : " + import.meta.env.VITE_APP_VERSION, ["App", "Version"]);
  if (localStorage.getItem("REACT_APP_STAGE")) return;
  console.log("no stage in localstorage set to demo");
  if (import.meta.env.VITE_APP_STAGE) {
    localStorage.setItem("REACT_APP_STAGE", import.meta.env.VITE_APP_STAGE);
    return;
  }
  localStorage.setItem("REACT_APP_STAGE", "demo");
}
registerListeners();
initSentry();

// Module-scope singleton, not created in the render body. Under capacitor-native-navigation
// each pushed view portals a fresh copy of this subtree from the one main webview, so a
// client created in render would be rebuilt on every one of the ~20 re-renders during
// launch, and each pushed view would get its own cache instead of sharing one.
const queryClient = new QueryClient();

/** The device's light/dark preference, kept current as it changes. */
function useDeviceScheme(): TColourSchemes {
  const query = useMemo(() => window.matchMedia("(prefers-color-scheme: dark)"), []);
  const [scheme, setScheme] = useState<TColourSchemes>(query.matches ? "dark" : "light");
  useEffect(() => {
    // Also catches iOS releasing the native style override when Device is picked, which is
    // when the webview first sees the real device setting again.
    const onChange = () => setScheme(query.matches ? "dark" : "light");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [query]);
  return scheme;
}

function useSyncTheme() {
  const [hydrated, setHydrated] = useState(false);
  const deviceScheme = useDeviceScheme();
  const { setDefaultTheme, setScheme, setOverride, setCustomId, setThemeImages } = useThemeContext();
  const orgConfig = useBoundStore((s) => s.orgConfig);
  // The theme the selected itinerary's tag names. Derived rather than read once, so every path that
  // selects an itinerary re-themes, including the store rehydrating from localStorage.
  const effectiveTheme = useBoundStore(selectEffectiveTheme);
  const hasHydrated = useStorageStore((s) => s.hasHydrated);
  const didApplyOnce = useRef(false);

  useEffect(() => {
    // Until the store is restored, the theme themeContext chose at launch stands.
    if (hasHydrated !== "finished") return;
    const storeDefaultTheme = (orgConfig?.themes || []).find((theme) => theme.default);

    const device = getDevicePrefName(deviceDetect);
    const storedTheme = getDevicePref("theme", device, "");
    const storedPalette = getDevicePref("themePalette", device, undefined);

    // Step 1: store default theme in context
    if (storeDefaultTheme) {
      setDefaultTheme(storeDefaultTheme);
    }

    // Step 2: Resolve the theme to apply.
    let finalTheme: TColourSchemes | "custom" = "light";
    let finalPalette: TOrgTheme | undefined = undefined;
    let override = false;

    if (!storedTheme || storedTheme === "") {
      // Device: a theme named by the itinerary's tag wins, else the OS light/dark setting. The
      // tag theme is stored so the next cold start paints it before the org config arrives.
      if (effectiveTheme) {
        finalTheme = "custom";
        finalPalette = effectiveTheme;
      } else {
        finalTheme = deviceScheme;
      }
    } else if (storedTheme === "custom") {
      finalTheme = "custom";
      finalPalette = storedPalette;
      override = true;
    } else {
      finalTheme = storedTheme;
      override = true;
    }

    const apply = () => {
      setScheme(finalTheme, finalPalette);
      setCustomId(finalTheme === "custom" ? (finalPalette?._id ?? "") : "");
      setThemeImages(finalTheme === "custom" ? (finalPalette?.images ?? {}) : {});
      setOverride(override);
      if (!override) {
        setThemeDevicePref(EFFECTIVE_THEME_PREF, finalPalette ?? "", device);
      }

      if (finalTheme === "custom" && finalPalette) {
        const lightBar = finalPalette?.palette?.navbar?.light ?? true;
        if (lightBar) {
          setStatusBarStyleLight();
        } else {
          setStatusBarStyleDark();
        }
      } else if (finalTheme === "light") {
        setStatusBarStyleLight();
      } else {
        setStatusBarStyleDark();
      }
    };

    // Step 3: Paint.
    const state = useThemeContext.getState();
    const target = themeSignature(finalTheme, finalPalette?._id ?? "");
    if (chooseApplyMode(didApplyOnce.current, themeSignature(state.selectedColourScheme, state.customThemeId), target) === "fade") {
      void useSkinFadeStore.getState().run(apply);
    } else {
      apply();
    }
    didApplyOnce.current = true;

    setHydrated(true);
  }, [
    hasHydrated,
    deviceScheme,
    effectiveTheme,
    orgConfig?.themes,
    setCustomId,
    setDefaultTheme,
    setOverride,
    setScheme,
    setThemeImages,
  ]);

  return hydrated;
}

function App() {
  const hasHydrated = useStorageStore((s) => s.hasHydrated);

  // Runs once per launch, not per render: App() re-renders roughly 20 times during
  // launch, once per native view under capacitor-native-navigation, and an unguarded
  // body would re-arm the 2s SplashScreen.hide() fallback each time. hideSplashOnce()
  // (splashController.ts) is the real splash owner; this is a once-only fallback in
  // case that path never fires.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Android's system bars are set up natively in MainActivity.setStatusBarOverlayWebView()
      // instead of here. The plugin call that used to live at this point,
      // SafeArea.setImmersiveNavigationBar(), hides Type.systemBars() wholesale — the status bar
      // along with the navigation bar — which left getSafeAreaInsets() reporting a top inset of
      // 0 and every screen drawing under the status bar.
      delay(2000).then(() => {
        SplashScreen.hide();
      });
      setCollectionEnabled(true);
    }
  }, []);

  setDefaultStage();
  // SPLOG floor: 0 debug, 4 warn.
  setSpLogLvl(DEBUG_STAGES.includes(reactAppStage()) ? 0 : 4);

  // Seed native permission state once at launch (reads only, no prompt) so the gated controls and the
  // reconcile/scheduler reflect the OS state. Once at mount rather than on every render.
  useEffect(() => {
    checkCalInitialPermissions();
    bs().syncNotificationPermissions();
  }, []);

  useEffect(() => {
    void sweepStaleImagePreviews();
  }, []);

  const selectedColourScheme = useThemeContext((s) => s.selectedColourScheme);
  const customThemeId = useThemeContext((s) => s.customThemeId);

  const themeHydrated = useSyncTheme();

  // Only regenerate theme on scheme, customThemeId, or insets changes.
  // generateTheme/themeOverride use global ColoursObj and are stable.
  const insets = useWindowStore((s) => s.windowSizes.insets?.insets);

  const mergedTheme = useMemo(() => {
    spLogTags("Theme recreated", ["THEME"], {
      customThemeId,
      insets,
      selectedColourScheme,
    });

    // The `as any` that used to sit on generateTheme's result is gone: MUI 9 accepts the
    // storybook Theme directly as createTheme's base. Keeping it would have suppressed
    // exactly the v9 drift this upgrade needed to see.
    return createTheme(generateTheme(ColoursObj), themeOverride(insets));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColourScheme, customThemeId, insets, themeHydrated]);

  if (hasHydrated !== "finished") {
    console.log("App.tsx hasHydrated is not finished");

    return <AppLoadingSpinner />;
  }

  return (
    <ThemeProvider theme={mergedTheme}>
      <I18nGate>
        {/* QueryClientProvider sits above the capacitor-native-navigation portal boundary
            (DevRoutesSplit/NativeNavigationRouter): everything below that point is portaled
            into a fresh subtree per pushed native view, so a provider placed below it would
            give every view its own query cache. React context passes through portals
            unaffected by the DOM re-parenting, so hoisting it here costs nothing and lets
            every view share one cache. RocketChatProvider stays below because it calls
            useNavigate() via useGlobalRouter, which needs the per-view <Router> that
            DevRoutesSplit sets up. */}
        <QueryClientProvider client={queryClient}>
          <ScrollWrapper>
            <DevRoutesSplit>
              <ErrorBoundary
                fallback={<ErrorPage />}
                onError={(error, errorInfo) => {
                  spErrorTags(error, ["ERROR", "ErrorBoundary"]);
                  spErrorTags(JSON.stringify(errorInfo, null, 2), ["ERROR", "ERROR INFO", "ErrorBoundary"]);
                  captureException(error, {
                    extra: {
                      note: "Created from ErrorBoundary. error: " + JSON.stringify(error),
                    },
                  });
                }}
              >
                <RocketChatProvider>
                  <AppChatProvider>
                    <OverlayWrapper>
                      <UsersRequiredAddiInfoDialogHandler />
                      <GuestRoutesSplit>
                        <LocalExpenseManager />
                        <FavouriteSyncManager />
                        <NoteSyncManager />
                        <SkinFadeLayer />
                        <ThemeRemountGate>
                          <LanguageRemountGate>
                            <AppRoutes />
                          </LanguageRemountGate>
                        </ThemeRemountGate>
                      </GuestRoutesSplit>
                    </OverlayWrapper>
                  </AppChatProvider>
                </RocketChatProvider>
              </ErrorBoundary>
            </DevRoutesSplit>
          </ScrollWrapper>
        </QueryClientProvider>
      </I18nGate>
    </ThemeProvider>
  );
}

/**
 * I18nGate suspends (via React 19 use()) until the i18n promise resolves, ensuring
 * I18nextProvider only mounts with a fully-initialized instance. Placed as a separate
 * component _below_ ThemeProvider so that MUI's ThemeProvider mounts first and initializes
 * its StyleRegistry before any suspension occurs. Suspending inside App() would pause the
 * entire tree including ThemeProvider, causing MUI CSS rules to get out of sync
 * (IndexSizeError) when the component remounts after suspension ends.
 */
function I18nGate({ children }: { children: React.ReactNode }) {
  const i18n = use(i18nPromise);
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

const FileViewerPage = lazy(() => import("./feat/fileViewer/FileViewerPage"));

/** The app, or in a tab the app opened to show a file, only the file. */
const AppRoot = () => (window.location.pathname === FILE_VIEWER_PATH ? <FileViewerPage /> : <App />);

export default AppRoot;
